import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import ChecklistModal from '../components/ChecklistModal';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import useScrollRestoration from '../hooks/useScrollRestoration';
import './EventsPage.css';

function groupByDay(events) {
  const groups = [];
  const seen = new Map();
  for (const event of events) {
    const key = event.event_date || 'sin-fecha';
    if (!seen.has(key)) {
      seen.set(key, groups.length);
      groups.push({ date: event.event_date, events: [] });
    }
    groups[seen.get(key)].events.push(event);
  }
  return groups;
}

function dayLabel(dateStr) {
  if (!dateStr) return 'Sin fecha';
  try {
    // Comparar como strings YYYY-MM-DD para evitar desfase de zona horaria
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth()+1).padStart(2,'0')}-${String(tomorrow.getDate()).padStart(2,'0')}`;

    const datePart = dateStr.split('T')[0];

    if (datePart === todayStr) return 'Hoy';
    if (datePart === tomorrowStr) return 'Mañana';

    // Para mostrar el nombre del día usar constructor local, sin UTC
    const [year, month, day] = datePart.split('-').map(Number);
    const d = new Date(year, month - 1, day);
    return format(d, "EEEE d 'de' MMMM", { locale: es });
  } catch { return dateStr; }
}

function daySubLabel(dateStr) {
  if (!dateStr) return '';
  try {
    const datePart = dateStr.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    const d = new Date(year, month - 1, day);
    return format(d, 'dd/MM/yyyy');
  } catch { return ''; }
}

export default function EventsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [search, setSearch] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [filterPlace, setFilterPlace] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  useScrollRestoration('events', !loading);

  const hasDateFilter = filterDateFrom || filterDateTo;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const archived = hasDateFilter ? 'all' : showArchived;
      const { data } = await api.get(`/events?archived=${archived}`);
      setEvents(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [showArchived, hasDateFilter]);

  useEffect(() => { load(); }, [load]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await api.post('/sync');
      await load();
    } catch (e) {
      alert('Error al sincronizar: ' + (e.response?.data?.error || e.message));
    } finally {
      setSyncing(false);
    }
  };

  const filtered = events.filter(e => {
    if (search) {
      const q = search.toLowerCase();
      const matchName = e.name.toLowerCase().includes(q);
      const matchPlace = e.place?.toLowerCase().includes(q);
      const matchEquipment = (e.equipment_raw || '').toLowerCase().includes(q);
      if (!matchName && !matchPlace && !matchEquipment) return false;
    }

    if (filterPlace) {
      if (!e.place?.toLowerCase().includes(filterPlace.toLowerCase())) return false;
    }

    if (filterDateFrom) {
      if (!e.event_date || e.event_date < filterDateFrom) return false;
    }

    if (filterDateTo) {
      if (!e.event_date || e.event_date > filterDateTo) return false;
    }

    return true;
  });

  const groups = groupByDay(filtered);
  const canOperate = user?.role === 'admin' || user?.role === 'deposito';

  return (
    <div className="events-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Eventos</h1>
          <p className="page-sub">
            {showArchived ? 'Historial archivado' : 'Próximos 14 días'}
          </p>
        </div>
        <div className="header-actions">
          <button
            onClick={() => setShowArchived(!showArchived)}
            className={`btn btn-ghost btn-sm ${showArchived ? 'active-tab' : ''}`}
          >
            {showArchived ? '← Activos' : 'Ver archivo'}
          </button>
          {user?.role === 'admin' && (
            <button onClick={handleSync} className="btn btn-primary btn-sm" disabled={syncing}>
              {syncing ? 'Sincronizando...' : '↻ Sincronizar'}
            </button>
          )}
        </div>
      </div>

      <div className="events-toolbar">
        <div className="search-input-wrapper">
          <input
            className="input"
            placeholder="Buscar evento o equipo..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <button
            className={`btn btn-ghost btn-sm search-advanced-btn ${showAdvanced ? 'active-tab' : ''}`}
            onClick={() => setShowAdvanced(!showAdvanced)}
          >
            Filtros
          </button>
        </div>

        {showAdvanced && (
          <div className="search-advanced-panel">
            <div className="form-group">
              <label className="form-label">Lugar</label>
              <input
                className="input"
                placeholder="Filtrar por lugar..."
                value={filterPlace}
                onChange={e => setFilterPlace(e.target.value)}
              />
            </div>
            <div className="search-date-row">
              <div className="form-group">
                <label className="form-label">Desde</label>
                <input
                  className="input"
                  type="date"
                  value={filterDateFrom}
                  onChange={e => setFilterDateFrom(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Hasta</label>
                <input
                  className="input"
                  type="date"
                  value={filterDateTo}
                  onChange={e => setFilterDateTo(e.target.value)}
                />
              </div>
            </div>
            {(filterPlace || filterDateFrom || filterDateTo) && (
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  setFilterPlace('');
                  setFilterDateFrom('');
                  setFilterDateTo('');
                }}
              >
                Limpiar filtros
              </button>
            )}
          </div>
        )}

        <span className="text-dim text-sm">
          {filtered.length} evento{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {loading ? (
        <div className="flex items-center" style={{ gap: 12, padding: '40px 0', color: 'var(--text-dim)' }}>
          <div className="spinner" style={{ width: 20, height: 20 }} />
          Cargando eventos...
        </div>
      ) : groups.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">◈</div>
          <p>No hay eventos {showArchived ? 'archivados' : 'próximos'}</p>
          {!showArchived && user?.role === 'admin' && (
            <button onClick={handleSync} className="btn btn-primary btn-sm mt-16">
              Sincronizar ahora
            </button>
          )}
        </div>
      ) : (
        <div className="day-groups">
          {groups.map((group, gi) => (
            <div key={gi} className="day-group">
              <div className="day-header">
                <span className="day-label">{dayLabel(group.date)}</span>
                <span className="day-sublabel">{daySubLabel(group.date)}</span>
                <span className="day-count text-mute text-xs">
                  {group.events.length} evento{group.events.length !== 1 ? 's' : ''}
                </span>
              </div>

              <div className="events-list">
                {group.events.map(event => (
                  <div
                    key={event.id}
                    className={`event-card ${event.status === 'possible' ? 'possible' : ''}`}
                    onClick={() => setSelectedEvent(event)}
                  >
                    <div className="event-top-row">
                      <div className="event-time">
                        {event.start_time && event.end_time
                          ? `${event.start_time} – ${event.end_time}hs`
                          : event.start_time
                            ? `${event.start_time}hs`
                            : ''
                        }
                      </div>
                      <div className="event-actions">
                        {event.status === 'possible' && (
                          <span className="badge badge-yellow">Posible</span>
                        )}
                        <div className="checklist-status">
                          {event.carga_status === 'closed'
                            ? <span className="badge badge-green">✓ Carga</span>
                            : event.carga_status === 'open'
                              ? <span className="badge badge-yellow">● Carga</span>
                              : canOperate && <span className="badge badge-gray">Carga</span>
                          }
                          {event.descarga_status === 'closed'
                            ? <span className="badge badge-green">✓ Descarga</span>
                            : event.descarga_status === 'open'
                              ? <span className="badge badge-yellow">● Descarga</span>
                              : canOperate && <span className="badge badge-gray">Descarga</span>
                          }
                        </div>
                      </div>
                    </div>

                    <div className="event-name">
                      {event.name.length > 60 ? event.name.substring(0, 60) + '...' : event.name}
                    </div>

                    {event.place && (
                      <div className="event-place">{event.place}</div>
                    )}

                    {event.assigned_people && (
                      <div className="event-people text-dim text-sm">
                        👤 {event.assigned_people}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedEvent && (
        <ChecklistModal
          event={selectedEvent}
          canOperate={canOperate}
          onClose={() => setSelectedEvent(null)}
          onOpenChecklist={(checklistId) => {
            setSelectedEvent(null);
            navigate(`/checklist/${checklistId}`);
          }}
        />
      )}
    </div>
  );
}
